import {
    camelToUpperSnake,
    camelToUpperSnakeArray,
    convertObjectPropertiesToCamelCase,
    upperSnakeToCamel,
    upperSnakeToCamelArray
} from './mappers';

export type Filter = {
    column: string;
    operator: string;
    value: any;
};

export type Sort = {
    column: string;
    direction: string;
};

export type ToCoreGridInput = {
    columns?: string[];
    filters?: Filter[][];
    items?: number;
    page?: number;
    sorts?: Sort[];
};

export type GridOutput<T> = {
    data: Partial<T>[];
    request: {
        items?: number;
        sorts?: Sort[];
        filters?: Filter[][];
        columns?: string[];
        page?: number;
    };
    totalCount: number;
};

type GridMapperDirection = 'core' | 'public';

const transformFilterValue = (value: any): any => {
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    if (Array.isArray(value)) {
        return value.map(transformFilterValue);
    }
    return value;
};

const getColumn = (direction: 'core' | 'public', column: string) =>
    direction === 'core' ? camelToUpperSnake(column) : upperSnakeToCamel(column);

const mapFilters = (
    direction: GridMapperDirection,
    filters?: Filter[][],
    omitColumns?: string[]
): Filter[][] | undefined =>
    filters?.map((filterGroup) =>
        filterGroup
            .filter((filter) => !omitColumns?.includes(filter.column))
            .map((filter) => ({
                ...filter,
                column: getColumn(direction, filter.column),
                value: transformFilterValue(filter.value)
            }))
    );

const mapSorts = (direction: GridMapperDirection, sorts?: Sort[], omitColumns?: string[]): Sort[] | undefined =>
    sorts
        ?.filter((sort) => !omitColumns?.includes(sort.column))
        .map((sort) => ({
            ...sort,
            column: getColumn(direction, sort.column)
        }));

export const mapToCoreGridInput = (input: ToCoreGridInput, omitColumns?: string[]): ToCoreGridInput => {
    const convertedOmitColumns = upperSnakeToCamelArray(omitColumns ?? []);
    const filteredColumns = input.columns?.filter((column) => !convertedOmitColumns.includes(column)) ?? [];

    return {
        columns: camelToUpperSnakeArray(filteredColumns),
        filters: mapFilters('core', input.filters, convertedOmitColumns),
        items: input.items,
        page: input.page,
        sorts: mapSorts('core', input.sorts, convertedOmitColumns)
    };
};

export const mapToPublicGridOutput = <T extends Record<string, any>, K extends keyof T>(
    coreData: GridOutput<T>,
    omitColumns: string[]
) => {
    const filteredColumns =
        coreData.request.columns?.filter((column) => !omitColumns.includes(upperSnakeToCamel(column))) ?? [];

    const camelCaseColumns = upperSnakeToCamelArray(filteredColumns);

    const filteredData = coreData.data.map((item) => convertObjectPropertiesToCamelCase(item, omitColumns));

    return {
        data: filteredData,
        request: {
            ...coreData.request,
            filters: mapFilters('public', coreData.request.filters, omitColumns),
            sorts: mapSorts('public', coreData.request.sorts, omitColumns),
            columns: camelCaseColumns
        },
        totalCount: coreData.totalCount
    };
};
